import { HEALTHY_INTERVAL_MS } from "../core/suppression.ts";
import type { BlinkEvent, PointerInputEvent } from "../core/types.ts";
import type { Mechanic, Readout } from "./mechanic.ts";
import { relief } from "./mechanic.ts";

/**
 * Mechanic A — blink as reload (design doc §4A).
 *
 * The thumb shoots; blinking is the only thing that puts rounds back. That is
 * the §2 shape: thumb is the high-frequency verb, blink is low-frequency and
 * high-impact. Blink at your natural rate and you never run dry; suppress and
 * you are empty at the worst moment. The incentive gradient points exactly
 * where the health outcome points, and nothing scores the blink itself.
 */

/**
 * Rounds in a full magazine, and the interval between shots.
 *
 * These two are one decision, not two, because the constraint that matters
 * couples them:
 *
 *   MAX_AMMO × COOLDOWN_MS >= HEALTHY_INTERVAL_MS
 *
 * A full magazine must take at least as long to spend as a resting adult takes
 * to blink once (4000ms). Without that, a player empties it in a second, spends
 * the rest of the interval dry, and starts *forcing* blinks to keep firing —
 * precisely the §1 violation the design doc exists to prevent.
 *
 * The first version satisfied it with 6 × 700ms. Measured tap gaps from real
 * sessions are p10 168ms, p50 250ms — people tap about four times a second in
 * bursts — so a 700ms cooldown rejected roughly two out of every three presses,
 * which does not read as a fire rate, it reads as a broken button.
 *
 * 15 × 270ms clears the same 4s floor while sitting just above the natural tap
 * interval, so the gun keeps up with the thumb instead of fighting it.
 * `reload.test.ts` asserts the inequality, so changing either number without
 * the other fails the suite rather than quietly making the game fight
 * physiology.
 */
const MAX_AMMO = 15;
const COOLDOWN_MS = 270;

/** Presses buffered between updates. Enough for a fast double-tap, not a stall. */
const MAX_QUEUED_SHOTS = 3;

/** How long the muzzle flash and recoil last. */
const FLASH_MS = 90;
const SHAKE_MS = 160;

export class Reload implements Mechanic {
  readonly id = "reload" as const;
  readonly hint = "tap anywhere to fire · blink to reload";

  /**
   * Fractional on purpose.
   *
   * The obvious version — `round(MAX_AMMO * amplitude / completeAmplitude)` —
   * rebuilds the exact cliff this mechanic was designed to avoid. Against the
   * amplitudes measured on a real session (0.47 0.52 0.65 0.67 0.68 0.70 0.72
   * 0.75 against a 0.70 reference) it yields 4, 4, 6, 6, 6, 6, 6, 6: fourteen
   * of fifteen samples land on one of two values and the boundary sits at
   * amplitude ≈ 0.58, right inside the cluster. That is the classifier coin
   * flip reintroduced one line after being avoided.
   *
   * Keeping ammo a float means a blink at 0.68 and one at 0.72 differ by six
   * percent, which is what design doc §5 actually asks for.
   */
  #ammo = MAX_AMMO;
  #queued = 0;
  #shots = 0;
  #dryFires = 0;
  #lastShotAt = Number.NEGATIVE_INFINITY;
  #flashUntil = 0;
  #shakeUntil = 0;

  constructor(private readonly target: HTMLElement) {}

  /**
   * Refills in proportion to how far the eyelid closed, and never past full.
   *
   * The clamp matters as much as the proportion: without it, blinking while
   * full banks ammo, and a game that lets you stockpile by blinking is a game
   * that rewards blinking. Clamped, a blink at full magazine gives literally
   * nothing — which is the correct feel. Blink naturally and you never notice
   * this mechanic; suppress and it is the only thing you notice.
   *
   * `event.kind` is never read. See the note on `#ammo`.
   */
  onBlink(event: BlinkEvent): void {
    this.#ammo = Math.min(MAX_AMMO, this.#ammo + MAX_AMMO * relief(event.amplitude));
  }

  /**
   * Queues a press. Decides nothing.
   *
   * A counter rather than the boolean latch `FatigueBlur` uses for jumps:
   * a boolean silently drops the second of two presses inside one frame, and
   * for a jump that is invisible while for a shooter it is a lost round. The
   * cap stops a backgrounded tab from dumping the magazine on resume.
   */
  onPointer(event: PointerInputEvent): void {
    if (event.phase !== "down") return;
    this.#queued = Math.min(MAX_QUEUED_SHOTS, this.#queued + 1);
  }

  update(t: number): void {
    // A press that arrives mid-cooldown WAITS. It used to be consumed and
    // thrown away, which at a 250ms tap interval meant most presses vanished
    // without a trace — the player is tapping, the gun is silent, and nothing
    // anywhere says why. Holding the press until the gun cycles turns a
    // rejection into a rhythm.
    if (this.#queued > 0 && t - this.#lastShotAt >= COOLDOWN_MS) {
      this.#queued--;
      if (this.#ammo >= 1) {
        this.#ammo -= 1;
        this.#shots++;
        this.#lastShotAt = t;
        this.#flashUntil = t + FLASH_MS;
      } else {
        // Nothing happens, and nothing else should. An escalating punishment
        // for being empty is a scored blink wearing a different hat; the
        // absence of the shot is the entire penalty.
        this.#dryFires++;
        this.#shakeUntil = t + SHAKE_MS;
      }
    }

    const empty = this.#ammo < 1;
    const flashing = t < this.#flashUntil;
    const shaking = t < this.#shakeUntil;

    // The ring reddens as the magazine drains, so pressure is visible before it
    // becomes a failure rather than arriving as a cliff.
    const fill = this.#ammo / MAX_AMMO;
    const hue = 140 * fill;
    const spread = flashing ? 26 : 8 + (1 - fill) * 10;
    const alpha = flashing ? 0.55 : 0.12 + (1 - fill) * 0.3;
    this.target.style.boxShadow = `inset 0 0 ${spread.toFixed(0)}px hsla(${hue.toFixed(0)}, 85%, 55%, ${alpha.toFixed(2)})`;

    this.target.style.transform = shaking
      ? `translateX(${(Math.sin((this.#shakeUntil - t) * 0.12) * 4).toFixed(2)}px)`
      : "";
    this.target.style.opacity = empty && !shaking ? "0.9" : "";
  }

  readout(): Readout {
    const empty = this.#ammo < 1;
    return {
      value: `${this.#ammo.toFixed(1)}/${MAX_AMMO}`,
      label: "ammo",
      ...(empty ? { alert: true } : {}),
    };
  }

  get ammo(): number {
    return this.#ammo;
  }

  get shotsFired(): number {
    return this.#shots;
  }

  get dryFires(): number {
    return this.#dryFires;
  }

  reset(): void {
    this.#ammo = MAX_AMMO;
    this.#queued = 0;
    this.#shots = 0;
    this.#dryFires = 0;
    this.#lastShotAt = Number.NEGATIVE_INFINITY;
    this.#flashUntil = 0;
    this.#shakeUntil = 0;
    this.target.style.boxShadow = "";
    this.target.style.transform = "";
    this.target.style.opacity = "";
  }
}

export { COOLDOWN_MS, HEALTHY_INTERVAL_MS, MAX_AMMO };
